import './event-details-modal.scss';
import JoinButton from '../join-button/join-button';
import PdfPreview from '../pdf-preview/pdf-preview';
import { formatEventDate, getEventSponsorLogo, hasEventEnded, isPdf } from '../../helpers/eventHelpers';
import { isMobileSafari } from '../../utils/browserUtils';
import type { CalendarEvent } from '../calendar/calendar.types';

type EventDetailsModalProps = {
  event: CalendarEvent;
  images: string[];
  currentImageIndex: number;
  onClose: () => void;
  onExpandImage: () => void;
  onChangeImageIndex: (direction: -1 | 1) => void;
};

const EventDetailsModal = ({
  event,
  images,
  currentImageIndex,
  onClose,
  onExpandImage,
  onChangeImageIndex,
}: EventDetailsModalProps) => {
  const currentImage = images[currentImageIndex];
  const useMobileSafariPdfButton = Boolean(currentImage && isPdf(currentImage) && isMobileSafari());

  return (
    <div
      className="meeting-calendar__modal"
      role="dialog"
      aria-modal="true"
      aria-label="Event details"
      onClick={onClose}
    >
      <div
        className="meeting-calendar__modal-content meeting-calendar__event-modal-content"
        onClick={(clickEvent) => clickEvent.stopPropagation()}
      >
        {currentImage && !useMobileSafariPdfButton && (
          <button
            type="button"
            className="meeting-calendar__modal-expand"
            onClick={onExpandImage}
            aria-label={`Expand image for ${event.title}`}
          >
            <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
              <path d="M8 4H20V16H18V7.41L5.71 19.71L4.29 18.29L16.59 6H8V4Z" fill="currentColor" />
            </svg>
          </button>
        )}
        <button
          type="button"
          className="meeting-calendar__modal-close"
          onClick={onClose}
          aria-label="Close event details"
        >
          ×
        </button>

        <div className="meeting-calendar__event-modal-image-wrap">
          {currentImage ? (
            <button
              type="button"
              onClick={(clickEvent) => {
                clickEvent.stopPropagation();
                onExpandImage();
              }}
              aria-label={useMobileSafariPdfButton ? `View event PDF for ${event.title}` : `Open full-size image for ${event.title}`}
              className={`meeting-calendar__event-modal-image-button${useMobileSafariPdfButton ? ' meeting-calendar__event-modal-image-button--view-event' : ''}`}
            >
              {useMobileSafariPdfButton ? (
                <span className="meeting-calendar__event-modal-view-label">View Event</span>
              ) : isPdf(currentImage) ? (
                <PdfPreview file={currentImage} className="pdf-preview--card" loadImmediately />
              ) : (
                <img className="meeting-calendar__event-modal-image" src={currentImage} alt={event.title} />
              )}
            </button>
          ) : (
            <div className="meeting-calendar__event-modal-image-placeholder">Event</div>
          )}
          {images.length > 1 && (
            <>
              <button
                type="button"
                className="meeting-calendar__event-modal-nav meeting-calendar__event-modal-nav--previous"
                onClick={(clickEvent) => {
                  clickEvent.stopPropagation();
                  onChangeImageIndex(-1);
                }}
                aria-label="Previous event image"
              >
                ‹
              </button>
              <button
                type="button"
                className="meeting-calendar__event-modal-nav meeting-calendar__event-modal-nav--next"
                onClick={(clickEvent) => {
                  clickEvent.stopPropagation();
                  onChangeImageIndex(1);
                }}
                aria-label="Next event image"
              >
                ›
              </button>
              <span className="meeting-calendar__event-modal-count">
                {currentImageIndex + 1} / {images.length}
              </span>
            </>
          )}
        </div>

        <div className="meeting-calendar__modal-body">
          {event.origin && (
            <span className="meeting-calendar__modal-origin">
              {event.origin === 'MSIA' ? 'MSIA Event' : `${event.origin} Event`}
            </span>
          )}

          <div className="meeting-calendar__modal-header meeting-calendar__modal-header--event">
            <h3>{event.title}</h3>
            {!!event.sponsors?.length && !hasEventEnded(event) && (
              <div className="meeting-calendar__event-sponsors" aria-label="Event sponsors">
                <span className="meeting-calendar__event-sponsors-title">Event Sponsors</span>
                <div className="meeting-calendar__event-sponsors-list">
                  <div className="meeting-calendar__event-sponsor">
                    {event.sponsors.map((sponsor) => {
                      const logo = getEventSponsorLogo(event, sponsor.logo);

                      return (
                        <div className="meeting-calendar__event-sponsor-entry" key={`${event.id}-${sponsor.name}`}>
                          {logo ? (
                            <img src={logo} alt={sponsor.name} className="meeting-calendar__event-sponsor-logo" />
                          ) : (
                            <span className="meeting-calendar__event-sponsor-logo meeting-calendar__event-sponsor-logo--placeholder">
                              Logo
                            </span>
                          )}
                          <span className="meeting-calendar__event-sponsor-name">
                            {sponsor.abbreviation || sponsor.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="meeting-calendar__modal-details meeting-calendar__modal-details--event">
            <span>{formatEventDate(event)}</span>
            {event.time && <span>{event.time}</span>}
            <span>{event.location}</span>
          </div>

          <p>{event.description}</p>

          <JoinButton link={event.link} />
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;
